import { EmptyState, NoDataIcon, PageHeader, Icon } from '@lmc/ui-kit'
import styled from 'styled-components'
import dayjs, { Dayjs } from 'dayjs'
import { useIncidentsReportList } from '../hooks'
import { FilterValues } from '../hooks/useIncidentsReportList'
import { useTranslation } from 'react-i18next'
import { Carer, Home, IncidentReportItem, UserRole } from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import { Button } from '@lmc/ui-kit/src/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { IncidentListItem } from './IncidentListItem'
import useFilterParamsGeneric from 'hooks/useFilterParamsGeneric'
import { Spinner } from 'components/generic'
import { useGenerateCsv } from 'screens/Reports/DownloadCentre/components/hooks'
import { CSV_EXPORT_TYPE } from 'screens/Reports/DownloadCentre/components/utils'
import { IncidentFilters } from './IncidentFilters'
import { SetFilterParams } from './IncidentsDashboard/IncidentsDashboard'
import { useEffect, useState } from 'react'
import { Pagination } from 'antd'

const initialFiltersValue: FilterValues = {
  rangePicker: [dayjs().subtract(4, 'week').startOf('day'), dayjs()],
  residents: [],
  locations: [],
  categoryItems: [],
  statuses: [],
  severities: [],
  resultedIn: [],
}

export type IncidentsReportMainProps = {
  title?: string
  description?: string
  hasResidentsFilter?: boolean
  hasHomesFilter?: boolean
  hasOnClick?: boolean
}

const CURRENT_PAGE = 1
const ITEMS_PER_PAGE = 10

export const IncidentsReportMain = ({
  title,
  description,
  hasResidentsFilter,
  hasHomesFilter,
  hasOnClick,
}: IncidentsReportMainProps) => {
  const { filterParamsGeneric, setFilterParams } =
    useFilterParamsGeneric(initialFiltersValue)

  const filterParams = filterParamsGeneric as FilterValues

  const [currentPage, setCurrentPage] = useState(CURRENT_PAGE)
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE)

  const handlePageChange = (page: number, pageSize?: number) => {
    setCurrentPage(page)
    if (pageSize) {
      setItemsPerPage(pageSize)
    }
  }

  const { data, isLoading, refetch } = useIncidentsReportList({
    ...filterParams,
    page: currentPage,
    itemsPerPage,
  })

  const incidents = data?.data || []
  const pagination = data?.pagination || {}

  useEffect(() => {
    refetch()
  }, [])

  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const user = useSelector(
    (state: { global: { user: Carer } }) => state.global.user
  )

  const hasIncidents = !!incidents?.length

  const { isLoading: isCSVLoading, generate } = useGenerateCsv(
    CSV_EXPORT_TYPE.INCIDENT_REPORTS,
    () => {
      showToast({
        title: `${t(incidentReportsConstants.EMAIL_SENT_TOAST_1)} ${
          user.email
        }. ${t(incidentReportsConstants.EMAIL_SENT_TOAST_2)}`,
      })
    }
  )

  const isGroupUser = [UserRole.groupAdmin, UserRole.groupManager]?.includes(
    user?.role
  )

  const getRangePicker = (
    rangePicker: [Dayjs | null, Dayjs | null] | undefined | null
  ) =>
    rangePicker && rangePicker[0] && rangePicker[1]
      ? [
          dayjs(rangePicker[0]).endOf('day').toISOString(),
          dayjs(rangePicker[1]).endOf('day').toISOString(),
        ]
      : undefined

  const csvExportFilters = {
    ...filterParamsGeneric,
    rangePicker: getRangePicker(filterParamsGeneric.rangePicker),
    ...(isGroupUser && {
      sites: filterParamsGeneric.homes
        ? filterParamsGeneric.homes
        : homes?.map((home) => home.id),
    }),
  }

  return (
    <>
      <PageHeader
        title={<TitleContainer>{title}</TitleContainer>}
        subtitle={description}
      >
        <Button variant="default" onClick={() => generate(csvExportFilters)}>
          <Icon name="DownloadOutlined" />
          <span>{`${
            isCSVLoading
              ? t(incidentReportsConstants.DOWNLOADING)
              : t(incidentReportsConstants.EXPORT_CSV)
          }`}</span>
        </Button>
      </PageHeader>
      <IncidentFilters
        filterParams={filterParams}
        setFilterParams={setFilterParams as SetFilterParams}
        hasResidentsFilter={hasResidentsFilter}
        hasHomesFilter={hasHomesFilter}
      />
      <ListItemContainer>
        {!isLoading &&
          hasIncidents &&
          incidents?.map((item: IncidentReportItem) => (
            <IncidentListItem
              incident={item}
              homes={hasHomesFilter ? homes : undefined}
              hasOnClick={hasOnClick}
            />
          ))}
      </ListItemContainer>
      {!isLoading && !hasIncidents && (
        <EmptyState
          title="Shoot... no data to show you"
          icon={<NoDataIcon />}
        />
      )}
      {isLoading && <Spinner />}
      <Pagination
        current={currentPage}
        pageSize={itemsPerPage}
        total={pagination.totalItems || 0}
        onChange={handlePageChange}
        showSizeChanger
        pageSizeOptions={['10', '20', '50']}
        style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}
      />
    </>
  )
}

const ListItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
